import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Ticker from "../components/Ticker";
import PipelineCanvas from "../components/PipelineCanvas";
import Footer from "../components/Footer";
import {
  Shield, Lock, Globe, Radar, ScanSearch, Sparkles, FileCheck2, Target,
  Layers, Bot, Terminal, BarChart3, Plus, Check, ArrowRight, Mail,
  ShieldCheck, Zap, Eye, Cpu, ArrowUpRight, Wrench, Workflow, Tag, HelpCircle, LogIn,
} from "lucide-react";

export default function Landing() {
  const [domain, setDomain] = useState("");
  const nav = useNavigate();

  const handleScan = () => {
    const t = domain.trim();
    if (!t) return;
    const token = localStorage.getItem("aegis_token");
    // Carry the target through signup. Dropping it here meant someone who had
    // already typed their domain had to remember and retype it after
    // registering — the intent was collected and then thrown away.
    if (!token) nav(`/register?target=${encodeURIComponent(t)}`);
    else nav(`/scan?target=${encodeURIComponent(t)}`);
  };

  return (
    <div className="au-root">
      {/* NAVBAR — mirrors the getkhojo.com landing bar: the middle grid column
          holds icon-logo-icon so the brand is truly centred, and the sign-in
          actions live out at the right edge instead of in the centre cluster. */}
      <nav className="au-nav">
        {/* left column: nav links, packed toward the centre */}
        <div className="au-navwing au-navwing-end">
          <NavBtn href="#tools" label="Tools" icon={<Wrench size={18} />} />
          <NavBtn href="#how" label="How it Works" icon={<Workflow size={18} />} />
        </div>

        {/* centre column: the brand itself */}
        <Link to="/" style={{ textDecoration: "none", display: "block" }} aria-label="GetKhojo home">
          {/* 8KB WebP with a 57KB PNG fallback. The original was a 1315x757,
              794KB master rendered at 60px tall - 71% of the whole page weight.
              width/height are set so the row reserves space and nothing shifts. */}
          <picture>
            <source srcSet="/Getkhojologobento.webp" type="image/webp" />
            <img src="/Getkhojologobento.png" alt="GetKhojo" width="104" height="60"
              style={{ display: "block", height: 60, width: "auto", borderRadius: 16 }} />
          </picture>
        </Link>

        {/* right column: remaining links, then the two account actions at the edge */}
        <div className="au-navwing au-navwing-start" style={{ justifyContent: "space-between" }}>
          <span className="au-navwing">
            <NavBtn href="#pricing" label="Pricing" icon={<Tag size={18} />} />
            <NavBtn href="#faq" label="FAQ" icon={<HelpCircle size={18} />} />
          </span>
          <span className="au-navwing">
            <NavBtn to="/login" label="Login" icon={<LogIn size={18} />} />
            <NavBtn to="/register" label="Get Started" icon={<ArrowRight size={18} />} primary />
          </span>
        </div>
      </nav>

      {/* HERO */}
      <section className="au-section au-reveal">
        <div className="au-container">
          <div
            className="au-grid"
            style={{ gridTemplateColumns: "minmax(0,1.1fr) minmax(0,0.9fr)", alignItems: "center" }}
          >
            {/* Copy */}
            <div style={{ maxWidth: 680 }}>
              {/* The live ticker used to sit here, but its feed is mostly legal
                  disclaimers — the worst possible thing to read directly above a
                  "Start Free Scan" button. It now runs above the footer, and the hero
                  leads with the differentiator instead. */}
              <span className="au-chip" style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
                <span className="au-led" />
                Passive checks only · nothing is ever exploited
              </span>
              <h1 style={{ fontSize: "var(--au-fs-h1)", marginBottom: 18 }}>
                Know your<br />
                <span className="au-em">attack surface</span><br />
                <span style={{ color: "var(--au-muted)", fontWeight: 400, fontSize: "0.55em" }}>
                  before hackers do
                </span>
              </h1>
              <p className="au-lead" style={{ maxWidth: 560, marginBottom: 28 }}>
                Aegis runs OWASP, SSL, DNS, port and CVE checks externally.
                Every finding is translated into plain English with exact fix steps.
                No agents. No server installation. Pure intelligence.
              </p>

              {/* SCAN BOX */}
              <div
                className="au-glass au-pad"
                style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: 10, maxWidth: 460 }}
              >
                <Terminal size={18} style={{ color: "var(--au-accent-ink)", flex: "none", marginLeft: 6 }} aria-hidden="true" />
                <input
                  className="au-input"
                  style={{ flex: 1, minWidth: 200, border: "none", outline: "none", boxShadow: "none", background: "transparent" }}
                  placeholder="enter domain or ip — example.com"
                  aria-label="Domain or IP to scan"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleScan()}
                />
                <button className="au-pill au-pill-primary" onClick={handleScan}>
                  <ScanSearch /> Start Free Scan
                </button>
              </div>

              {/* SAMPLE REPORT */}
              <a
                href="https://getkhojo.com/aegis-sample-report.pdf"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 7, marginTop: 10, padding: "10px 2px", minHeight: 44, fontSize: "var(--au-fs-body)", fontWeight: 600, color: "var(--au-accent-ink)", textDecoration: "none" }}
              >
                <FileCheck2 size={15} /> See a sample report <ArrowUpRight size={15} />
              </a>

              {/* STATS */}
              <div style={{ display: "flex", gap: 36, flexWrap: "wrap", marginTop: 34 }}>
                <Stat n="7+" l="Detection Layers" />
                <Stat n="<60s" l="To First Report" />
                <Stat n="6" l="Report Formats" />
                <Stat n="BETA" l="Early Access" />
              </div>
            </div>

            {/* SIGNATURE NODE CANVAS — scan pipeline */}
            <PipelineCanvas
              className="au-pipeline-sm"
              ariaLabel="Aegis scan pipeline: a target fans out to SSL/TLS, DNS, Ports and CVE checks, which converge into a single report"
              nodes={[
                { id: "target", x: 16, y: 50, icon: <Target />,      title: "Target",    sub: "domain / ip" },
                { id: "ssl",    x: 50, y: 18, icon: <Lock />,        title: "SSL / TLS", sub: "ciphers · expiry" },
                { id: "dns",    x: 50, y: 39, icon: <Globe />,       title: "DNS",       sub: "SPF · DMARC" },
                { id: "ports",  x: 50, y: 61, icon: <Radar />,       title: "Ports",     sub: "services · banners" },
                { id: "cve",    x: 50, y: 82, icon: <ShieldCheck />, title: "CVE",       sub: "known exploits" },
                { id: "report", x: 84, y: 50, icon: <FileCheck2 />,  title: "Report",    sub: "fix steps" },
              ]}
              edges={[
                { from: "target", to: "ssl" },
                { from: "target", to: "dns" },
                { from: "target", to: "ports" },
                { from: "target", to: "cve" },
                { from: "ssl",   to: "report" },
                { from: "dns",   to: "report" },
                { from: "ports", to: "report" },
                { from: "cve",   to: "report" },
              ]}
            />
          </div>
        </div>
      </section>

      {/* TOOLS / ARSENAL */}
      <section id="tools" className="au-section au-reveal">
        <div className="au-container">
          <div className="au-center" style={{ marginBottom: 32 }}>
            <span className="au-eyebrow">Our Arsenal</span>
            <h2 style={{ fontSize: "var(--au-fs-h2)", marginTop: 10 }}>
              Complete <span className="au-em">Security Toolkit</span>
            </h2>
            <p className="au-lead" style={{ marginTop: 10 }}>4 powerful tools. One platform. Zero compromise.</p>
          </div>
          <div className="au-grid au-cols-4">
            {[
              { Icon: Shield, name: "Attack Surface", desc: "OWASP Top 10, CVE matching, exposed endpoints discovery", badge: "FLAGSHIP", cat: "au-cat-green" },
              { Icon: Lock, name: "SSL/TLS Analyzer", desc: "Certificate, ciphers, HSTS, expiry, protocol vulnerabilities", badge: "FREE", cat: "au-cat-blue" },
              { Icon: Globe, name: "DNS Inspector", desc: "SPF, DMARC, DKIM, zone transfer, hijacking detection", badge: "FREE", cat: "au-cat-amber" },
              { Icon: Radar, name: "Port Intelligence", desc: "Service fingerprinting, banner grabbing, hidden services", badge: "PRO", cat: "au-cat-gray" },
            ].map((t, i) => (
              <div key={i} className="au-glass au-pad au-lift">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
                  <span className="au-badge"><t.Icon /></span>
                  <span className={`au-chip au-chip-cat ${t.cat}`}>{t.badge}</span>
                </div>
                <h3 style={{ fontSize: "var(--au-fs-h3)", fontWeight: 700, marginBottom: 10 }}>{t.name}</h3>
                <p style={{ fontSize: "var(--au-fs-body)", color: "var(--au-text-2)", lineHeight: 1.7 }}>{t.desc}</p>
                <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--au-hair)" }}>
                  <Link to="/register" className="au-pill au-pill-glass au-pill-sm">
                    Start Free Scan <ArrowRight />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="au-section au-reveal">
        <div className="au-container">
          <div className="au-center" style={{ marginBottom: 32 }}>
            <span className="au-eyebrow">Simple Process</span>
            <h2 style={{ fontSize: "var(--au-fs-h2)", marginTop: 10 }}>
              How <span className="au-em">It Works</span>
            </h2>
          </div>
          <div className="au-grid au-cols-3">
            {[
              { n: "01", Icon: Terminal, t: "Enter Target", d: "Type any domain or IP you own or have authorization to test. No server installation required." },
              { n: "02", Icon: Cpu, t: "Automated Scans", d: "Distributed scanners run OWASP, SSL, DNS, port and CVE checks in parallel." },
              { n: "03", Icon: BarChart3, t: "Get Report", d: "Risk score, plain English findings, fix steps. Download as PDF, CSV, or DOC." },
            ].map((s, i) => (
              <div key={i} className="au-glass au-pad-lg au-lift au-center">
                <div className="au-mono" style={{ fontSize: "var(--au-fs-meta)", color: "var(--au-accent-ink)", letterSpacing: 2.5, marginBottom: 16 }}>
                  STEP {s.n}
                </div>
                <span className="au-badge au-badge-dark" style={{ margin: "0 auto 16px" }}><s.Icon /></span>
                <h3 style={{ fontSize: "var(--au-fs-h3)", fontWeight: 700, marginBottom: 10 }}>{s.t}</h3>
                <p style={{ fontSize: "var(--au-fs-body)", color: "var(--au-text-2)", lineHeight: 1.7 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="au-section au-reveal">
        <div className="au-container" style={{ maxWidth: 800 }}>
          <div className="au-center" style={{ marginBottom: 40 }}>
            <span className="au-eyebrow">Questions?</span>
            <h2 style={{ fontSize: "var(--au-fs-h2)", marginTop: 10 }}>
              Frequently <span className="au-em">Asked</span>
            </h2>
          </div>
          {[
            { q: "Is Aegis legal to use?", a: "Yes. Public scanner runs only passive checks (SSL, headers, DNS) — same as any browser. For Security Check/active scans, we require domain ownership verification and authorization letter." },
            { q: "Do I need to install anything?", a: "No. Aegis runs externally from our servers. Just enter your domain and we do the rest." },
            { q: "How fast is a scan?", a: "Most scans complete in under 90 seconds. Heavy scans take up to 5 minutes." },
            { q: "What payment methods?", a: "Stripe (international cards), Razorpay (UPI, Indian cards, netbanking). All receipts emailed instantly." },
            { q: "Can I cancel anytime?", a: "Yes. No contracts. Cancel anytime from dashboard. Refund within 7 days if unused." },
          ].map((f, i) => (
            <details key={i} className="au-glass" style={{ marginBottom: 10, overflow: "hidden" }}>
              <summary
                style={{ padding: "16px 20px", cursor: "pointer", fontSize: "var(--au-fs-lead)", color: "var(--au-text)", fontWeight: 600, listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}
              >
                {f.q}
                <Plus size={18} style={{ color: "var(--au-accent-ink)", flex: "none" }} aria-hidden="true" />
              </summary>
              <div style={{ padding: "4px 20px 18px", fontSize: "var(--au-fs-body)", color: "var(--au-text-2)", lineHeight: 1.7 }}>{f.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* PRICING — a left rail of tiers driving one detail panel on the right.
          Six stacked cards ran 1,304px (28% of the page) and asked the reader to
          diff six feature lists at once; this shows one tier at a time and keeps
          the section to roughly the height of a single card. */}
      <section id="pricing" className="au-section au-reveal">
        <div className="au-container">
          <div className="au-center" style={{ marginBottom: 32 }}>
            <span className="au-eyebrow">Simple Pricing</span>
            <h2 style={{ fontSize: "var(--au-fs-h2)", marginTop: 10 }}>
              Choose Your <span className="au-em">Power Level</span>
            </h2>
            <p className="au-lead" style={{ marginTop: 10 }}>No hidden fees. Cancel anytime. INR &amp; USD supported.</p>
          </div>
          <PricingPicker />
        </div>
      </section>

      {/* TRUST BAR */}
      <div className="au-container" style={{ marginBottom: 8 }}>
        <div
          className="au-glass au-pad"
          style={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 18, alignItems: "center" }}
        >
          {[
            { Icon: ShieldCheck, t: "OWASP Top 10" },
            { Icon: Globe, t: "Global Ready" },
            { Icon: Cpu, t: "Automated" },
            { Icon: Mail, t: "Email Reports" },
            { Icon: Lock, t: "Privacy First" },
            { Icon: Layers, t: "Auto-Cleanup" },
            { Icon: Zap, t: "Real-time" },
          ].map((i) => (
            <div key={i.t} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--au-fs-body)", fontWeight: 600, color: "var(--au-text-2)" }}>
              <i.Icon size={16} style={{ color: "var(--au-accent-ink)" }} aria-hidden="true" />
              {i.t}
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <section className="au-section au-reveal" style={{ paddingTop: 30 }}>
        <div className="au-container">
          <div className="au-glass au-pad-lg au-center" style={{ maxWidth: 800, margin: "0 auto", boxShadow: "var(--au-shadow), var(--au-glow)" }}>
            <span className="au-badge au-badge-dark" style={{ margin: "0 auto 18px" }}><Eye /></span>
            <h2 style={{ fontSize: 32, fontWeight: 700, marginBottom: 14 }}>
              Ready to <span className="au-em">Secure Your Future</span>?
            </h2>
            <p className="au-lead" style={{ marginBottom: 24 }}>
              Join the early access program. Your first scan is free.
            </p>
            <Link to="/register" className="au-pill au-pill-primary" style={{ padding: "15px 40px", fontSize: 16 }}>
              <ScanSearch /> Start Free Scan
            </Link>
          </div>
        </div>
      </section>

      {/* LIVE TICKER — blocked attempts, safety and legal notices. Kept on the
          page (it is genuinely protective) but below the conversion path. */}
      <div className="au-container" style={{ marginBottom: 28 }}>
        <Ticker variant="bar" />
      </div>

      {/* FOOTER */}
      <Footer />
    </div>
  );
}

/* Icon-only nav control. The visible label is a hover/focus tooltip, so
   `aria-label` is the ONLY accessible name — without it these are unlabelled
   to a screen reader. The tooltip stays aria-hidden to avoid announcing twice. */
/* ---------------- Pricing ---------------- */
const PLANS = [
  { name: "FREE", price: "$0", inr: "₹0", per: "/week", cta: "Start Free Scan", best: "Kicking the tyres on one site", feats: ["1 scan / week", "First scan shown in full", "Later scans: names only", "PDF report only", "No emailed report"] },
  { name: "ENTRY", price: "$5", inr: "₹449", per: "single scan", cta: "Buy one scan", best: "A one-off check before a launch", feats: ["Pay per scan", "Critical & High visible", "PDF + HTML download", "Emailed report", "No commitment"] },
  { name: "STARTER", price: "$25", inr: "₹1,999", per: "/month", cta: "Choose Starter", best: "One site, watched every month", feats: ["7 scans / month", "Full findings + remediation code", "PDF + HTML + DOCX + CSV", "+1 free OSINT / mo (PDF)", "Emailed report"] },
  { name: "PRO", price: "$99", inr: "₹7,999", per: "/month", popular: true, cta: "Choose Pro", best: "A few sites, on a schedule", feats: ["30 scans / month", "Scheduled scans", "PDF + CSV + DOCX", "+3 free OSINT / mo (PDF + HTML)", "Company-domain guard"] },
  { name: "MAX", price: "$250", inr: "₹19,999", per: "/month", cta: "Choose Max", best: "High volume across a portfolio", feats: ["100 scans / month", "Scheduled scans", "PDF + CSV + DOCX", "+3 free OSINT / mo (PDF + HTML)", "Company-domain guard"] },
  { name: "AGENCY", price: "Let’s talk", cta: "Schedule a call", schedule: true, per: "", best: "Reselling under your own brand", feats: ["White-label", "Full API access", "Team seats + SLA", "Priority support", "Custom volume"] },
];

function PricingPicker() {
  // Opens on the badged tier: it anchors value, and anyone after the free plan
  // reads the rail top-down anyway.
  const [sel, setSel] = useState(PLANS.findIndex((p) => p.popular));
  const tabRefs = useRef([]);
  const p = PLANS[sel];

  /* Tablist keyboard contract: arrows move, Home/End jump. Without this the
     rail is mouse-only, since the buttons carry role="tab". */
  const onKey = (e) => {
    const last = PLANS.length - 1;
    let next = null;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") next = sel === last ? 0 : sel + 1;
    else if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = sel === 0 ? last : sel - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    if (next === null) return;
    e.preventDefault();
    setSel(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <div className="au-pricing">
      <div className="au-pricing-rail" role="tablist" aria-label="Pricing plans" aria-orientation="vertical" onKeyDown={onKey}>
        {PLANS.map((pl, i) => (
          <button
            key={pl.name}
            ref={(el) => { tabRefs.current[i] = el; }}
            role="tab"
            id={`plan-tab-${i}`}
            aria-selected={i === sel}
            aria-controls="plan-panel"
            tabIndex={i === sel ? 0 : -1}
            onClick={() => setSel(i)}
            className={`au-plan-tab${i === sel ? " is-active" : ""}`}
          >
            <span className="au-plan-tab-name">
              {pl.name}
              {pl.popular && <span className="au-plan-pop"><Sparkles size={11} /> Popular</span>}
            </span>
            <span className="au-plan-tab-price">{pl.price}<span className="au-plan-tab-per">{pl.per}</span></span>
          </button>
        ))}
      </div>

      <div className="au-glass au-pad-lg au-pricing-panel" role="tabpanel" id="plan-panel"
        aria-labelledby={`plan-tab-${sel}`} tabIndex={0}>
        <div className="au-eyebrow" style={{ marginBottom: 14 }}>{p.name}</div>
        <div style={{ marginBottom: 4 }}>
          <span className="au-stat-num" style={{ fontSize: 46 }}>{p.price}</span>
          <span style={{ color: "var(--au-muted)", marginLeft: 6, fontSize: "var(--au-fs-meta)" }}>{p.per}</span>
        </div>
        <div className="au-mono" style={{ fontSize: "var(--au-fs-meta)", color: "var(--au-muted)", marginBottom: 12 }}>
          {p.inr ? `${p.inr} ${p.per}` : "Custom quote"}
        </div>
        <div style={{ fontSize: "var(--au-fs-body)", color: "var(--au-text-2)", marginBottom: 20, lineHeight: 1.45 }}>
          {p.best}
        </div>
        <div style={{ marginBottom: 22 }}>
          {p.feats.map((f) => (
            <div key={f} style={{ padding: "10px 0", borderBottom: "1px solid var(--au-hair)", fontSize: "var(--au-fs-body)", color: "var(--au-text-2)", display: "flex", alignItems: "center", gap: 10 }}>
              <Check size={15} style={{ color: "var(--au-accent-ink)", flex: "none" }} aria-hidden="true" />
              {f}
            </div>
          ))}
        </div>
        <Link to={p.schedule ? "/contact" : "/register"} className="au-pill au-pill-primary" style={{ width: "100%" }}>
          {p.cta} <ArrowRight />
        </Link>
      </div>
    </div>
  );
}

function NavBtn({ to, href, label, icon, primary = false }) {
  const cls = `au-navbtn${primary ? " au-navbtn-primary" : ""}`;
  // The label is real text inside the button (revealed by the expand), so it is
  // already the accessible name — an aria-label here would override and
  // duplicate it. `title` is likewise omitted: it would fight the expand.
  const inner = (
    <>
      {icon}
      <span className="au-navlabel">{label}</span>
    </>
  );
  return to
    ? <Link to={to} className={cls}>{inner}</Link>
    : <a href={href} className={cls}>{inner}</a>;
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="au-stat-num">{n}</div>
      <div className="au-stat-lbl">{l}</div>
    </div>
  );
}
